/**
 * Per-panel collapsed/expanded persistence for the designer's collapsible
 * panels (issue #189). One key per panel so a single wedged panel can be
 * cleared without resetting the whole layout.
 *
 * NAMING: `label-designer.*`, the convention every other UI preference in the
 * app already uses (`label-designer.preview.profileId`,
 * `label-designer.locale`, `label-designer.theme`,
 * `label-designer.cmdk.recent`). The competing `ld:` prefix exists only on
 * SplitPane's two split-ratio keys and is the minority convention.
 *
 * Shape and try/catch discipline mirror preview/profile-storage.ts, which in
 * turn mirrors SplitPane's readStored/persist pair: a private-mode browser or
 * a full quota must never crash a panel.
 */
export function panelCollapseKey(panelId: string): string {
  return `label-designer.panels.${panelId}.collapsed`
}

/**
 * Whether `panelId` was last left collapsed.
 *
 * Anything that is not the exact string `'true'` reads as EXPANDED: a missing
 * key, a corrupt or legacy value, or a localStorage that throws. Defaulting
 * the other way could wedge a panel shut with its own expand affordance
 * hidden — there would be nothing left on screen to click.
 */
export function readPanelCollapsed(panelId: string): boolean {
  try {
    return localStorage.getItem(panelCollapseKey(panelId)) === 'true'
  } catch {
    return false
  }
}

export function persistPanelCollapsed(panelId: string, collapsed: boolean): void {
  try {
    localStorage.setItem(panelCollapseKey(panelId), collapsed ? 'true' : 'false')
  } catch {
    // ignore (private mode, quota)
  }
}
